import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getRentalBookings } from "../../../../services/rentalService";
import Dialog from "../../../dialog/Dialog";
import Pagination from "../../../common/Pagination";
import Toster from "../../../common/Toster";
import { AdminPageCard, AdminPageCardToolbar } from "../../../common/AdminPageLayout";
import { getTableEmptyProps } from "../../../common/supplierTableEmpty";
import {
  SupplierFilterToolbar,
  SupplierFilterField,
} from "../../../common/supplierPageUi";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  TableSearchInput,
  TableEmptyRow,
  ViewDetailsButton,
  TableActionsCell,
} from "../../../common/adminTable";
import { getPricingFeeFields } from "../BookingPricingFees";
import {
  formatDate,
  formatDateTime,
  formatTime,
  getPaymentStatusColor,
  selectedRowClass,
} from "../bookingDetailHelpers";
import {
  BookingDetailHeader,
  BookingDetailSection,
  BookingDetailFields,
  BookingDetailTable,
  BookingDetailMetaChip,
  BookingDetailBadge,
  BookingDetailUserCard,
  BookingDetailNav,
  BookingPaymentProgress,
  CopyableText,
} from "../BookingDetailUi";

const PER_PAGE = 10;

const SECTION_IDS = {
  overview: "rental-booking-overview",
  payment: "rental-booking-payment",
  customer: "rental-booking-customer",
};

const NAV_ITEMS = [
  { id: SECTION_IDS.overview, label: "Overview", icon: "fi fi-rr-apps" },
  { id: SECTION_IDS.payment, label: "Payments", icon: "fi fi-rr-credit-card" },
  { id: SECTION_IDS.customer, label: "Customer", icon: "fi fi-rr-user" },
];

const rentalBookingPayments = (b) =>
  (Array.isArray(b?.rental_payments) && b.rental_payments) ||
  (Array.isArray(b?.rentalPayments) && b.rentalPayments) ||
  [];

const rentalPaidTotal = (b) =>
  rentalBookingPayments(b)
    .filter((p) => String(p?.status || "").toLowerCase() === "completed")
    .reduce((sum, p) => sum + (parseFloat(p?.amount) || 0), 0);

const rentalBookingTotal = (b) => parseFloat(b?.total_price) || 0;

const rentalBalance = (b) => {
  const total = rentalBookingTotal(b);
  const paid = rentalPaidTotal(b);
  return Math.max(0, Math.round((total - paid) * 100) / 100);
};

const RentalBookings = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [activeSection, setActiveSection] = useState(SECTION_IDS.overview);
  const [copyToast, setCopyToast] = useState(null);

  const {
    data: rows = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["rentalBookingsSupplier"],
    queryFn: async () => {
      const res = await getRentalBookings();
      return Array.isArray(res?.data) ? res.data : [];
    },
  });

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((b) => {
      const title = String(b?.item?.title || "").toLowerCase();
      const userName = String(b?.user?.name || "").toLowerCase();
      const userEmail = String(b?.user?.email || "").toLowerCase();
      const idStr = String(b?.id ?? "");
      const status = String(b?.status || "").toLowerCase();
      return (
        title.includes(q) ||
        userName.includes(q) ||
        userEmail.includes(q) ||
        idStr.includes(q) ||
        status.includes(q)
      );
    });
  }, [rows, searchQuery]);

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
    setCurrentPage((p) => Math.min(p, lastPage));
  }, [filtered.length]);

  const pagination = useMemo(() => {
    const total = filtered.length;
    const lastPage = Math.max(1, Math.ceil(total / PER_PAGE));
    const page = Math.min(currentPage, lastPage);
    const from = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
    const to = Math.min(page * PER_PAGE, total);
    const start = (page - 1) * PER_PAGE;
    const data = filtered.slice(start, start + PER_PAGE);
    return { currentPage: page, lastPage, perPage: PER_PAGE, total, from, to, data };
  }, [filtered, currentPage]);

  const handlePageChange = (page) => {
    if (page !== pagination.currentPage) setCurrentPage(page);
  };

  const handleViewDetails = (booking) => {
    setSelectedBookingId(booking.id);
    setActiveSection(SECTION_IDS.overview);
    setShowDetailsDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDetailsDialog(false);
    setSelectedBookingId(null);
    setActiveSection(SECTION_IDS.overview);
  };

  const handleCopied = useCallback((label, type = "success") => {
    setCopyToast({ message: `${label} copied to clipboard`, type });
    setTimeout(() => setCopyToast(null), 2500);
  }, []);

  const scrollToSection = useCallback((sectionId) => {
    setActiveSection(sectionId);
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    });

  const getPaymentStatus = (booking) => {
    const balance = rentalBalance(booking);
    const paid = rentalPaidTotal(booking);
    if (balance === 0 && paid > 0) return "Paid";
    if (paid > 0) return "Partially Paid";
    return "Unpaid";
  };

  const selectedBooking =
    rows.find((b) => b.id === selectedBookingId) ||
    filtered.find((b) => b.id === selectedBookingId);

  const payments = selectedBooking ? rentalBookingPayments(selectedBooking) : [];

  const safeText = (value, fallback = "—") => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === "string" && value.trim() === "") return fallback;
    return String(value);
  };

  return (
    <>
      {copyToast && (
        <Toster
          message={copyToast.message}
          type={copyToast.type}
          duration={2500}
          onClose={() => setCopyToast(null)}
        />
      )}

      <AdminPageCard>
        <AdminPageCardToolbar>
          <SupplierFilterToolbar
            showReset={!!searchQuery}
            onReset={() => {
              setSearchQuery("");
              setCurrentPage(1);
            }}
          >
            <SupplierFilterField label="Search">
              <TableSearchInput
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search rental bookings..."
                className="mt-1 w-full sm:w-full"
              />
            </SupplierFilterField>
          </SupplierFilterToolbar>
        </AdminPageCardToolbar>

        {isError ? (
          <div className="flex items-center justify-between gap-3 border-b border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span>
              {error?.response?.data?.message || error?.message || "Failed to load rental bookings."}
            </span>
            <button
              type="button"
              onClick={() => refetch()}
              className="shrink-0 text-sm font-semibold text-primary hover:underline"
            >
              Retry
            </button>
          </div>
        ) : null}

        <AdminTable className="shadow-none rounded-none">
          <AdminTableHead>
            <AdminTableTh>#</AdminTableTh>
            <AdminTableTh>Rental</AdminTableTh>
            <AdminTableTh>Customer</AdminTableTh>
            <AdminTableTh>Start</AdminTableTh>
            <AdminTableTh>End</AdminTableTh>
            <AdminTableTh>Total</AdminTableTh>
            <AdminTableTh>Paid</AdminTableTh>
            <AdminTableTh>Balance</AdminTableTh>
            <AdminTableTh>Status</AdminTableTh>
            <AdminTableTh align="right">Actions</AdminTableTh>
          </AdminTableHead>
          <AdminTableBody>
            {isLoading ? (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-sm text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : pagination.data.length > 0 ? (
              pagination.data.map((booking, idx) => (
                <AdminTableRow
                  key={booking.id}
                  className={selectedRowClass(
                    selectedBookingId === booking.id && showDetailsDialog
                  )}
                >
                  <AdminTableCell>
                    {(pagination.currentPage - 1) * pagination.perPage + idx + 1}
                  </AdminTableCell>
                  <AdminTableCell nowrap={false}>
                    <div className="font-medium">{booking?.item?.title || "Rental"}</div>
                  </AdminTableCell>
                  <AdminTableCell>
                    {booking?.user?.name || booking?.user?.email || "Customer"}
                  </AdminTableCell>
                  <AdminTableCell>{formatDateTime(booking.start_datetime)}</AdminTableCell>
                  <AdminTableCell>{formatDateTime(booking.end_datetime)}</AdminTableCell>
                  <AdminTableCell>{formatCurrency(rentalBookingTotal(booking))}</AdminTableCell>
                  <AdminTableCell>{formatCurrency(rentalPaidTotal(booking))}</AdminTableCell>
                  <AdminTableCell>{formatCurrency(rentalBalance(booking))}</AdminTableCell>
                  <AdminTableCell>
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ring-1 ring-inset ${getPaymentStatusColor(
                        getPaymentStatus(booking)
                      )}`}
                    >
                      {getPaymentStatus(booking)}
                    </span>
                  </AdminTableCell>
                  <TableActionsCell>
                    <ViewDetailsButton
                      onClick={() => handleViewDetails(booking)}
                      ariaLabel={`View details for booking ${booking.id}`}
                    />
                  </TableActionsCell>
                </AdminTableRow>
              ))
            ) : (
              <TableEmptyRow
                colSpan={10}
                {...getTableEmptyProps("rentalBookings", {
                  hasActiveFilters: !!searchQuery,
                  onClearFilters: () => setSearchQuery(""),
                })}
              />
            )}
          </AdminTableBody>
        </AdminTable>
      </AdminPageCard>

      {showDetailsDialog && selectedBooking && (
        <Dialog
          title="Rental Booking Details"
          open={showDetailsDialog}
          onClose={handleCloseDialog}
          width="w-full"
          variant="detail"
          showCloseButton={false}
        >
          <div className="space-y-5">
            <BookingDetailNav items={NAV_ITEMS} activeId={activeSection} onSelect={scrollToSection} />

            <BookingDetailHeader
              bookingId={selectedBooking.id}
              title={selectedBooking?.item?.title || "Rental"}
              onCopyBookingId={handleCopied}
              meta={
                <>
                  <BookingDetailMetaChip icon="fi fi-rr-calendar">
                    {formatDateTime(selectedBooking.start_datetime)} –{" "}
                    {formatDateTime(selectedBooking.end_datetime)}
                  </BookingDetailMetaChip>
                  <BookingDetailMetaChip icon="fi fi-rr-marker">
                    {safeText(selectedBooking.pickup_location, "Pickup N/A")}
                  </BookingDetailMetaChip>
                </>
              }
              badges={
                <>
                  <BookingDetailMetaChip icon="fi fi-rr-info">
                    Booking: {safeText(selectedBooking.status)}
                  </BookingDetailMetaChip>
                  <BookingDetailMetaChip icon="fi fi-rr-shield-check">
                    Deposit: {safeText(selectedBooking.deposit_status)}
                  </BookingDetailMetaChip>
                  <BookingDetailMetaChip icon="fi fi-rr-undo">
                    Return: {safeText(selectedBooking.return_status)}
                  </BookingDetailMetaChip>
                </>
              }
              status={getPaymentStatus(selectedBooking)}
              statusClassName={getPaymentStatusColor(getPaymentStatus(selectedBooking))}
              amount={formatCurrency(rentalBookingTotal(selectedBooking))}
              bookedAt={formatDate(selectedBooking.created_at)}
              bookedTime={formatTime(selectedBooking.created_at)}
              stats={[
                { label: "Paid", value: formatCurrency(rentalPaidTotal(selectedBooking)) },
                {
                  label: "Balance",
                  value: formatCurrency(rentalBalance(selectedBooking)),
                  highlight: rentalBalance(selectedBooking) > 0,
                },
                { label: "Pickup km", value: safeText(selectedBooking.pickup_km), icon: "fi fi-rr-marker" },
                { label: "Return km", value: safeText(selectedBooking.return_km), icon: "fi fi-rr-flag" },
              ]}
            />

            <BookingPaymentProgress
              total={rentalBookingTotal(selectedBooking)}
              paid={rentalPaidTotal(selectedBooking)}
              balance={rentalBalance(selectedBooking)}
              formatCurrency={formatCurrency}
            />

            <div id={SECTION_IDS.overview} className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <BookingDetailSection title="Booking Information" icon="fi fi-rr-receipt">
                <BookingDetailFields
                  onCopied={handleCopied}
                  fields={[
                    {
                      label: "Booking ID",
                      value: `#${selectedBooking.id}`,
                      copyable: true,
                      copyValue: selectedBooking.id,
                    },
                    {
                      label: "Pickup Location",
                      value: safeText(selectedBooking.pickup_location),
                      fullWidth: true,
                    },
                    {
                      label: "Drop-off Location",
                      value: safeText(selectedBooking.dropoff_location),
                      fullWidth: true,
                    },
                    {
                      label: "Start",
                      value: formatDateTime(selectedBooking.start_datetime),
                    },
                    {
                      label: "End",
                      value: formatDateTime(selectedBooking.end_datetime),
                    },
                    {
                      label: "Total Amount",
                      value: formatCurrency(rentalBookingTotal(selectedBooking)),
                      highlight: true,
                    },
                    {
                      label: "Discount",
                      value: formatCurrency(selectedBooking.discount_amount ?? 0),
                    },
                    ...getPricingFeeFields(selectedBooking, formatCurrency, 18),
                    {
                      label: "Paid Amount",
                      value: formatCurrency(rentalPaidTotal(selectedBooking)),
                      highlight: rentalBalance(selectedBooking) === 0,
                    },
                    {
                      label: "Balance",
                      value: formatCurrency(rentalBalance(selectedBooking)),
                    },
                    { label: "Pickup km", value: safeText(selectedBooking.pickup_km) },
                    { label: "Return km", value: safeText(selectedBooking.return_km) },
                    { label: "Driven km", value: safeText(selectedBooking.driven_km) },
                    {
                      label: "Document",
                      value: safeText(selectedBooking.document),
                      fullWidth: true,
                    },
                  ]}
                />
              </BookingDetailSection>

              <BookingDetailSection title="Rental Item" icon="fi fi-rr-car">
                <BookingDetailFields
                  fields={[
                    {
                      label: "Title",
                      value: selectedBooking.item?.title || "—",
                      fullWidth: true,
                    },
                    { label: "Item ID", value: selectedBooking.item?.id || "—" },
                    {
                      label: "Booking Status",
                      value: safeText(selectedBooking.status),
                    },
                    {
                      label: "Deposit Status",
                      value: safeText(selectedBooking.deposit_status),
                    },
                    {
                      label: "Return Status",
                      value: safeText(selectedBooking.return_status),
                    },
                  ]}
                />
              </BookingDetailSection>
            </div>

            <BookingDetailSection
              id={SECTION_IDS.payment}
              title="Payment History"
              icon="fi fi-rr-credit-card"
              action={
                payments.length > 0 ? (
                  <span className="text-[10px] font-medium text-gray-400">
                    {payments.length} transaction{payments.length === 1 ? "" : "s"}
                  </span>
                ) : null
              }
            >
              <BookingDetailTable
                columns={["Amount", "Status", "Method", "Order ID", "Payment ID"]}
                emptyTitle="No payments yet"
                emptyMessage="Payment records will appear here once transactions are completed."
                rows={payments.map((p) => (
                  <tr key={p.id} className="transition hover:bg-gray-50/80">
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                      {formatCurrency(p.amount)}
                    </td>
                    <td className="px-4 py-3">
                      <BookingDetailBadge
                        className={
                          String(p.status).toLowerCase() === "completed"
                            ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
                            : "bg-amber-50 text-amber-700 ring-amber-600/20"
                        }
                      >
                        {safeText(p.status)}
                      </BookingDetailBadge>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                      {safeText(p.payment_method)}
                    </td>
                    <td className="max-w-[160px] px-4 py-3">
                      <CopyableText
                        value={p.order_id}
                        label="Order ID"
                        onCopied={handleCopied}
                        mono
                        className="font-mono text-xs text-gray-500"
                      />
                    </td>
                    <td className="max-w-[160px] px-4 py-3">
                      <CopyableText
                        value={p.payment_id}
                        label="Payment ID"
                        onCopied={handleCopied}
                        mono
                        className="font-mono text-xs text-gray-500"
                      />
                    </td>
                  </tr>
                ))}
              />
            </BookingDetailSection>

            <BookingDetailSection id={SECTION_IDS.customer} title="User Details" icon="fi fi-rr-user">
              <BookingDetailUserCard user={selectedBooking.user} onCopied={handleCopied} />
            </BookingDetailSection>
          </div>
        </Dialog>
      )}

      <div className="mt-4">
        <Pagination pagination={pagination} onPageChange={handlePageChange} showInfo={true} />
      </div>
    </>
  );
};

export default RentalBookings;
